/** Maintenance plans: list and editor dialog (triggers, assignments, target preview). */
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  type AssignmentIn,
  EVENT_TRIGGER_TYPES,
  type Plan,
  type PlanIn,
  type PlanPatch,
  TRIGGER_KINDS,
  type TriggerIn,
  usePlans,
  usePreviewTargets,
  useSavePlan,
} from "@/features/admin/maintenance/queries";
import { useAssetTypesAdmin, useMeterTypes } from "@/features/admin/reference/queries";
import { WO_PRIORITIES } from "@/features/workorders/queries";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader, StatusBadge } from "@/shared/components/page";
import {
  AssetSelect,
  AssetTypePicker,
  JobTemplatePicker,
  SitePicker,
} from "@/shared/components/pickers";
import { localId } from "@/shared/components/sortable-list";
import { nullable, numberOrNull, useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";
import { Badge } from "@/shared/ui/primitives";

interface PlanValues extends Record<string, unknown> {
  code: string;
  name: string;
  job_template_id: string;
  priority: string;
  lead_time_days: string;
  reminder_days_before: string;
  meter_lead: string;
  tolerance_days: string;
  meter_tolerance: string;
  meter_recurrence_policy: "fixed" | "completion_relative";
  notes: string;
  is_active: boolean;
}

/** Editable trigger row. */
export interface TriggerRow {
  _id: string;
  kind: string;
  calendar_unit: "days" | "months";
  interval_days: string;
  interval_months: string;
  rrule: string;
  floating: boolean;
  meter_type_id: string;
  meter_interval: string;
  event_type: string;
  event_offset_days: string;
  condition_meter_type: string;
  condition_op: string;
  condition_value: string;
}

/** Editable assignment row. */
export interface AssignmentRow {
  _id: string;
  mode: "type" | "asset";
  asset_id: string;
  asset_type_id: string;
  site_id: string;
  criticality: string;
  tag: string;
}

const newTrigger = (): TriggerRow => ({
  _id: localId(),
  kind: "calendar",
  calendar_unit: "days",
  interval_days: "30",
  interval_months: "3",
  rrule: "",
  floating: true,
  meter_type_id: "",
  meter_interval: "",
  event_type: "asset.installed",
  event_offset_days: "0",
  condition_meter_type: "",
  condition_op: "gt",
  condition_value: "",
});
const newAssignment = (): AssignmentRow => ({
  _id: localId(),
  mode: "type",
  asset_id: "",
  asset_type_id: "",
  site_id: "",
  criticality: "",
  tag: "",
});

/** Convert API plan rows to editable rows. */
export function planToRows(plan: Plan | undefined): {
  triggers: TriggerRow[];
  assignments: AssignmentRow[];
} {
  return {
    triggers: (plan?.triggers ?? []).map((tr) => {
      const cond = (tr.condition ?? {}) as { meter_type?: string; op?: string; value?: number };
      return {
        _id: tr.id,
        kind: tr.kind,
        calendar_unit: monthlyInterval(tr.rrule) ? "months" : "days",
        interval_days:
          tr.interval_days === null || tr.interval_days === undefined
            ? ""
            : String(tr.interval_days),
        interval_months: String(monthlyInterval(tr.rrule) ?? 3),
        rrule: tr.rrule ?? "",
        floating: tr.floating,
        meter_type_id: tr.meter_type_id ?? "",
        meter_interval: tr.meter_interval ?? "",
        event_type: tr.event_type || "asset.installed",
        event_offset_days: String(tr.event_offset_days ?? 0),
        condition_meter_type: cond.meter_type ?? "",
        condition_op: cond.op ?? "gt",
        condition_value: cond.value === undefined ? "" : String(cond.value),
      };
    }),
    assignments: (plan?.assignments ?? []).map((a) => {
      const filter = (a.filter ?? {}) as { site_id?: string; criticality?: string; tag?: string };
      return {
        _id: a.id,
        mode: a.asset_id ? "asset" : "type",
        asset_id: a.asset_id ?? "",
        asset_type_id: a.asset_type_id ?? "",
        site_id: filter.site_id ?? "",
        criticality: filter.criticality ?? "",
        tag: filter.tag ?? "",
      };
    }),
  };
}

/** Read a monthly recurrence interval from its RRULE. */
export function monthlyInterval(rrule: string | null | undefined): number | null {
  if (!rrule || !/(?:^|;)FREQ=MONTHLY(?:;|$)/i.test(rrule)) {
    return null;
  }
  const match = /(?:^|;)INTERVAL=(\d+)(?:;|$)/i.exec(rrule);
  const interval = Number(match?.[1] ?? 1);
  return interval > 0 ? interval : null;
}

/** Preview calendar-month due dates while clamping at the target month's final day. */
export function previewCalendarMonths(anchor: string, intervalMonths: number): string[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(anchor) || intervalMonths < 1) {
    return [];
  }
  const [yearText, monthText, dayText] = anchor.split("-");
  let year = Number(yearText);
  let month = Number(monthText) - 1;
  let day = Number(dayText);
  const dates: string[] = [];
  for (let occurrence = 1; occurrence <= 3; occurrence += 1) {
    const monthIndex = year * 12 + month + intervalMonths;
    const targetYear = Math.floor(monthIndex / 12);
    const targetMonth = monthIndex % 12;
    const finalDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
    const targetDay = Math.min(day, finalDay);
    const targetDate = new Date(Date.UTC(targetYear, targetMonth, targetDay));
    dates.push(targetDate.toISOString().slice(0, 10));
    year = targetYear;
    month = targetMonth;
    day = targetDay;
  }
  return dates;
}

/** Serialise rows into API payloads. */
export function rowsToPayload(
  triggers: TriggerRow[],
  assignments: AssignmentRow[],
): { triggers: TriggerIn[]; assignments: AssignmentIn[] } {
  return {
    triggers: triggers.map((tr) => {
      const usesCalendarMonths = tr.calendar_unit === "months";
      return {
        kind: tr.kind,
        interval_days:
          tr.kind === "calendar" && !usesCalendarMonths ? numberOrNull(tr.interval_days) : null,
        rrule:
          tr.kind === "calendar" && usesCalendarMonths
            ? `FREQ=MONTHLY;INTERVAL=${Number(tr.interval_months) || 3}`
            : tr.kind === "calendar"
              ? monthlyInterval(tr.rrule) === null
                ? tr.rrule
                : ""
              : "",
        floating: tr.floating,
        meter_type_id: tr.kind === "meter" ? nullable(tr.meter_type_id) : null,
        meter_interval: tr.kind === "meter" ? numberOrNull(tr.meter_interval) : null,
        condition:
          tr.kind === "condition"
            ? {
                meter_type: tr.condition_meter_type,
                op: tr.condition_op,
                value: Number(tr.condition_value),
              }
            : {},
        event_type: tr.kind === "event" ? tr.event_type : "",
        event_offset_days: tr.kind === "event" ? Number(tr.event_offset_days) || 0 : 0,
      };
    }),
    assignments: assignments.map((a) => {
      const filter: Record<string, string> = {};
      if (a.mode === "type") {
        if (a.site_id) {
          filter.site_id = a.site_id;
        }
        if (a.criticality) {
          filter.criticality = a.criticality;
        }
        if (a.tag.trim()) {
          filter.tag = a.tag.trim();
        }
      }
      return {
        asset_id: a.mode === "asset" ? nullable(a.asset_id) : null,
        asset_type_id: a.mode === "type" ? nullable(a.asset_type_id) : null,
        filter,
      };
    }),
  };
}

/** Plan create/edit dialog. */
export function PlanDialog({ plan, onClose }: { plan?: Plan; onClose: () => void }): ReactElement {
  const { t } = useTranslation();
  const save = useSavePlan();
  const meterTypes = useMeterTypes();
  const rows = useMemo(() => planToRows(plan), [plan]);
  const [triggers, setTriggers] = useState<TriggerRow[]>(
    rows.triggers.length ? rows.triggers : [newTrigger()],
  );
  const [assignments, setAssignments] = useState<AssignmentRow[]>(
    rows.assignments.length ? rows.assignments : [newAssignment()],
  );
  const [previewStart, setPreviewStart] = useState(() => new Date().toISOString().slice(0, 10));
  const preview = usePreviewTargets(rowsToPayload([], assignments).assignments);
  const initial = useMemo<PlanValues>(
    () => ({
      code: plan?.code ?? "",
      name: plan?.name ?? "",
      job_template_id: plan?.job_template_id ?? "",
      priority: plan?.priority ?? "normal",
      lead_time_days: String(plan?.lead_time_days ?? 7),
      reminder_days_before: String(plan?.reminder_days_before ?? 14),
      meter_lead: plan?.meter_lead ?? "",
      tolerance_days: String(plan?.tolerance_days ?? 7),
      meter_tolerance: plan?.meter_tolerance ?? "",
      meter_recurrence_policy:
        plan?.meter_recurrence_policy === "fixed" ? "fixed" : "completion_relative",
      notes: plan?.notes ?? "",
      is_active: plan?.is_active ?? true,
    }),
    [plan],
  );
  const form = useForm<PlanValues>({
    initial,
    schema: z.object({
      code: plan ? z.string() : z.string().trim().min(1, t("app.required")),
      name: z.string().trim().min(1, t("app.required")),
      job_template_id: z.string().min(1, t("app.required")),
    }),
    idPrefix: "plan",
  });
  const updateTrigger = (id: string, patch: Partial<TriggerRow>): void =>
    setTriggers(triggers.map((x) => (x._id === id ? { ...x, ...patch } : x)));
  const updateAssignment = (id: string, patch: Partial<AssignmentRow>): void =>
    setAssignments(assignments.map((x) => (x._id === id ? { ...x, ...patch } : x)));
  const hasMeterTrigger = triggers.some((x) => x.kind === "meter");
  const hasMonthlyTrigger = triggers.some(
    (trigger) => trigger.kind === "calendar" && trigger.calendar_unit === "months",
  );

  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const payload = rowsToPayload(triggers, assignments);
      const common = {
        name: v.name.trim(),
        job_template_id: v.job_template_id,
        priority: v.priority,
        lead_time_days: Number(v.lead_time_days) || 0,
        reminder_days_before: Number(v.reminder_days_before) || 0,
        meter_lead: numberOrNull(v.meter_lead),
        tolerance_days: Number(v.tolerance_days) || 0,
        meter_tolerance: numberOrNull(v.meter_tolerance) ?? 0,
        meter_recurrence_policy: v.meter_recurrence_policy,
        notes: v.notes,
        triggers: payload.triggers,
        assignments: payload.assignments,
      };
      const body: PlanIn | PlanPatch = plan
        ? { ...common, is_active: v.is_active }
        : { ...common, code: v.code.trim() };
      await save.mutateAsync({ id: plan?.id, body });
    });
    if (ok) {
      onClose();
    }
  };

  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={plan ? t("plans.edit") : t("plans.new")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      wide
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="plan-code" label={t("app.code")} error={form.errors.code}>
          <Input {...form.field("code")} disabled={!!plan} />
        </Field>
        <Field id="plan-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} />
        </Field>
        <Field
          id="plan-job_template_id"
          label={t("jobTemplates.title")}
          error={form.errors.job_template_id}
        >
          <JobTemplatePicker
            id="plan-job_template_id"
            value={form.values.job_template_id}
            onChange={(id) => form.set("job_template_id", id ?? "")}
            required
          />
        </Field>
        <Field id="plan-priority" label={t("app.priority")}>
          <Select
            id="plan-priority"
            value={form.values.priority}
            onChange={(e) => form.set("priority", e.target.value)}
          >
            {WO_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {t(`workOrders.priority.${p}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="plan-lead_time_days" label={t("plans.leadTime")} hint={t("plans.leadTimeHint")}>
          <Input type="number" min="0" {...form.field("lead_time_days")} />
        </Field>
        <Field
          id="plan-tolerance_days"
          label={t("plans.tolerance")}
          hint={t("plans.toleranceHint")}
        >
          <Input type="number" min="0" {...form.field("tolerance_days")} />
        </Field>
        <Field
          id="plan-reminder_days_before"
          label={t("plans.reminderDaysBefore")}
          hint={t("plans.reminderDaysBeforeHint")}
        >
          <Input type="number" min="0" {...form.field("reminder_days_before")} />
        </Field>
        {hasMeterTrigger ? (
          <>
            <Field
              id="plan-meter_lead"
              label={t("plans.meterLead")}
              hint={t("plans.meterLeadHint")}
            >
              <Input type="number" step="any" min="0" {...form.field("meter_lead")} />
            </Field>
            <Field
              id="plan-meter_tolerance"
              label={t("plans.meterTolerance")}
              hint={t("plans.meterToleranceHint")}
            >
              <Input type="number" step="any" min="0" {...form.field("meter_tolerance")} />
            </Field>
            <Field
              id="plan-meter_recurrence_policy"
              label={t("plans.meterRecurrencePolicy")}
              hint={t("plans.meterRecurrencePolicyHint")}
            >
              <Select
                id="plan-meter_recurrence_policy"
                value={form.values.meter_recurrence_policy}
                onChange={(event) =>
                  form.set(
                    "meter_recurrence_policy",
                    event.target.value as "fixed" | "completion_relative",
                  )
                }
              >
                <option value="fixed">{t("plans.meterRecurrence.fixed")}</option>
                <option value="completion_relative">
                  {t("plans.meterRecurrence.completion_relative")}
                </option>
              </Select>
            </Field>
          </>
        ) : null}
      </div>

      {hasMonthlyTrigger ? (
        <Field id="plan-preview_start" label={t("plans.previewStart")}>
          <Input
            id="plan-preview_start"
            type="date"
            value={previewStart}
            onChange={(event) => setPreviewStart(event.target.value)}
          />
        </Field>
      ) : null}

      <div className="space-y-2 rounded-md border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{t("plans.triggers")}</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setTriggers([...triggers, newTrigger()])}
          >
            {t("plans.addTrigger")}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t("plans.triggersHint")}</p>
        {triggers.map((tr, i) => (
          <div
            key={tr._id}
            className="grid grid-cols-1 gap-2 border-t pt-2 sm:grid-cols-[10rem_minmax(0,1fr)_auto]"
          >
            <Select
              aria-label={`${t("plans.triggerKind")} ${i + 1}`}
              value={tr.kind}
              onChange={(e) => updateTrigger(tr._id, { kind: e.target.value })}
            >
              {TRIGGER_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`plans.triggerKinds.${k}`)}
                </option>
              ))}
            </Select>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {tr.kind === "calendar" ? (
                <>
                  <Select
                    aria-label={`${t("plans.calendarIntervalUnit")} ${i + 1}`}
                    className="w-44"
                    value={tr.calendar_unit}
                    onChange={(event) => {
                      const calendar_unit = event.target.value === "months" ? "months" : "days";
                      updateTrigger(tr._id, {
                        calendar_unit,
                        interval_days: tr.interval_days || "90",
                        interval_months: tr.interval_months || "3",
                      });
                    }}
                  >
                    <option value="days">{t("plans.calendarDays")}</option>
                    <option value="months">{t("plans.calendarMonths")}</option>
                  </Select>
                  {tr.calendar_unit === "months" ? (
                    <>
                      <span className="text-sm">{t("plans.every")}</span>
                      <Select
                        aria-label={`${t("plans.intervalMonths")} ${i + 1}`}
                        className="w-24"
                        value={tr.interval_months}
                        onChange={(event) =>
                          updateTrigger(tr._id, { interval_months: event.target.value })
                        }
                      >
                        <option value="3">3</option>
                        <option value="6">6</option>
                      </Select>
                      <span className="text-sm">{t("plans.months")}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm">{t("plans.every")}</span>
                      <Input
                        aria-label={`${t("plans.intervalDays")} ${i + 1}`}
                        type="number"
                        min="1"
                        className="w-24"
                        value={tr.interval_days}
                        onChange={(event) =>
                          updateTrigger(tr._id, { interval_days: event.target.value })
                        }
                      />
                      <span className="text-sm">{t("plans.days")}</span>
                    </>
                  )}
                  <Checkbox
                    id={`trigger-${tr._id}-floating`}
                    label={t("plans.floating")}
                    checked={tr.floating}
                    onChange={(e) => updateTrigger(tr._id, { floating: e.target.checked })}
                  />
                </>
              ) : null}
              {tr.kind === "meter" ? (
                <>
                  <span className="text-sm">{t("plans.every")}</span>
                  <Input
                    aria-label={`${t("plans.meterInterval")} ${i + 1}`}
                    type="number"
                    step="any"
                    min="0"
                    className="w-24"
                    value={tr.meter_interval}
                    onChange={(e) => updateTrigger(tr._id, { meter_interval: e.target.value })}
                  />
                  <Select
                    aria-label={`${t("assetTypes.meterType")} ${i + 1}`}
                    className="w-48"
                    value={tr.meter_type_id}
                    onChange={(e) => updateTrigger(tr._id, { meter_type_id: e.target.value })}
                  >
                    <option value="">—</option>
                    {(meterTypes.data ?? []).map((mt) => (
                      <option key={mt.id} value={mt.id}>
                        {mt.name} ({mt.unit})
                      </option>
                    ))}
                  </Select>
                </>
              ) : null}
              {tr.kind === "event" ? (
                <>
                  <Select
                    aria-label={`${t("plans.eventType")} ${i + 1}`}
                    className="w-56"
                    value={tr.event_type}
                    onChange={(e) => updateTrigger(tr._id, { event_type: e.target.value })}
                  >
                    {EVENT_TRIGGER_TYPES.map((ev) => (
                      <option key={ev} value={ev}>
                        {t(`plans.events.${ev}`, { defaultValue: ev })}
                      </option>
                    ))}
                  </Select>
                  <span className="text-sm">+</span>
                  <Input
                    aria-label={`${t("plans.offsetDays")} ${i + 1}`}
                    type="number"
                    className="w-20"
                    value={tr.event_offset_days}
                    onChange={(e) => updateTrigger(tr._id, { event_offset_days: e.target.value })}
                  />
                  <span className="text-sm">{t("plans.days")}</span>
                </>
              ) : null}
              {tr.kind === "condition" ? (
                <>
                  <Select
                    aria-label={`${t("assetTypes.meterType")} ${i + 1}`}
                    className="w-44"
                    value={tr.condition_meter_type}
                    onChange={(e) =>
                      updateTrigger(tr._id, { condition_meter_type: e.target.value })
                    }
                  >
                    <option value="">—</option>
                    {(meterTypes.data ?? []).map((mt) => (
                      <option key={mt.key} value={mt.key}>
                        {mt.name}
                      </option>
                    ))}
                  </Select>
                  <Select
                    aria-label={`${t("plans.operator")} ${i + 1}`}
                    className="w-20"
                    value={tr.condition_op}
                    onChange={(e) => updateTrigger(tr._id, { condition_op: e.target.value })}
                  >
                    <option value="gt">&gt;</option>
                    <option value="gte">≥</option>
                    <option value="lt">&lt;</option>
                    <option value="lte">≤</option>
                  </Select>
                  <Input
                    aria-label={`${t("plans.threshold")} ${i + 1}`}
                    type="number"
                    step="any"
                    className="w-24"
                    value={tr.condition_value}
                    onChange={(e) => updateTrigger(tr._id, { condition_value: e.target.value })}
                  />
                </>
              ) : null}
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label={`${t("app.delete")} ${t("plans.triggers")} ${i + 1}`}
              disabled={triggers.length === 1}
              onClick={() => setTriggers(triggers.filter((x) => x._id !== tr._id))}
            >
              ×
            </Button>
          </div>
        ))}
        {hasMonthlyTrigger ? (
          <div className="rounded-md bg-muted/40 p-2 text-sm" role="status">
            {triggers
              .filter(
                (trigger) => trigger.kind === "calendar" && trigger.calendar_unit === "months",
              )
              .map((trigger) => {
                const interval = Number(trigger.interval_months) || 3;
                return (
                  <p key={trigger._id}>
                    {t("plans.recurrencePreview", {
                      months: interval,
                      dates: previewCalendarMonths(previewStart, interval).join(", "),
                    })}
                  </p>
                );
              })}
          </div>
        ) : null}
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{t("plans.assignments")}</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setAssignments([...assignments, newAssignment()])}
          >
            {t("plans.addAssignment")}
          </Button>
        </div>
        {assignments.map((a, i) => (
          <div
            key={a._id}
            className="grid grid-cols-1 gap-2 border-t pt-2 sm:grid-cols-[9rem_minmax(0,1fr)_auto]"
          >
            <Select
              aria-label={`${t("plans.assignmentMode")} ${i + 1}`}
              value={a.mode}
              onChange={(e) =>
                updateAssignment(a._id, { mode: e.target.value as "type" | "asset" })
              }
            >
              <option value="type">{t("plans.byType")}</option>
              <option value="asset">{t("plans.byAsset")}</option>
            </Select>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {a.mode === "asset" ? (
                <AssetSelect
                  id={`assign-${a._id}-asset`}
                  value={a.asset_id}
                  onChange={(id) => updateAssignment(a._id, { asset_id: id ?? "" })}
                />
              ) : (
                <>
                  <AssetTypePicker
                    id={`assign-${a._id}-type`}
                    value={a.asset_type_id}
                    onChange={(id) => updateAssignment(a._id, { asset_type_id: id ?? "" })}
                  />
                  <SitePicker
                    id={`assign-${a._id}-site`}
                    value={a.site_id}
                    onChange={(id) => updateAssignment(a._id, { site_id: id ?? "" })}
                    placeholder={t("plans.anySite")}
                  />
                  <Select
                    aria-label={`${t("assets.criticality")} ${i + 1}`}
                    value={a.criticality}
                    onChange={(e) => updateAssignment(a._id, { criticality: e.target.value })}
                  >
                    <option value="">{t("plans.anyCriticality")}</option>
                    {["low", "medium", "high"].map((c) => (
                      <option key={c} value={c}>
                        {t(`assets.criticalityLevel.${c}`)}
                      </option>
                    ))}
                  </Select>
                  <Input
                    aria-label={`${t("plans.tagFilter")} ${i + 1}`}
                    placeholder={t("plans.tagFilter")}
                    value={a.tag}
                    onChange={(e) => updateAssignment(a._id, { tag: e.target.value })}
                  />
                </>
              )}
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label={`${t("app.delete")} ${t("plans.assignments")} ${i + 1}`}
              disabled={assignments.length === 1}
              onClick={() => setAssignments(assignments.filter((x) => x._id !== a._id))}
            >
              ×
            </Button>
          </div>
        ))}
        <p className="text-sm" role="status">
          {preview.data ? (
            <>
              <strong>{t("plans.targetCount", { count: preview.data.total })}</strong>
              {preview.data.sample.length ? (
                <span className="text-muted-foreground">
                  {" "}
                  — {preview.data.sample.map((s) => s.code).join(", ")}
                  {preview.data.total > preview.data.sample.length ? "…" : ""}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-muted-foreground">{t("plans.targetHint")}</span>
          )}
        </p>
      </div>

      <Field id="plan-notes" label={t("app.notes")}>
        <Textarea {...form.field("notes")} />
      </Field>
      {plan ? (
        <Checkbox
          id="plan-is_active"
          label={t("app.active")}
          checked={form.values.is_active}
          onChange={(e) => form.set("is_active", e.target.checked)}
        />
      ) : null}
    </FormDialog>
  );
}

/** Plans admin page. */
export function PlansAdminPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const plans = usePlans();
  const assetTypes = useAssetTypesAdmin();
  const [dialog, setDialog] = useState<{ plan?: Plan } | null>(null);
  const manage = can("maintenance.plan.manage");
  const describeTrigger = (tr: Plan["triggers"][number]): string => {
    switch (tr.kind) {
      case "calendar":
        return t("plans.everyDays", { count: tr.interval_days ?? 0 });
      case "meter":
        return `${t("plans.every")} ${tr.meter_interval ?? "?"} ${tr.meter_type_key ?? ""}`;
      case "event":
        return tr.event_type || "event";
      default:
        return t("plans.triggerKinds.condition");
    }
  };
  const columns: ColumnDef<Plan>[] = [
    {
      id: "code",
      header: t("app.code"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.code}</span>,
    },
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => (
        <span className={c.row.original.is_active ? "font-medium" : "font-medium line-through"}>
          {c.row.original.name}
        </span>
      ),
    },
    {
      id: "template",
      header: t("jobTemplates.title"),
      cell: (c) => c.row.original.job_template_name,
    },
    {
      id: "triggers",
      header: t("plans.triggers"),
      cell: (c) => (
        <span className="flex flex-wrap gap-1">
          {c.row.original.triggers.map((tr, i) => (
            <Badge key={i} tone="outline">
              {describeTrigger(tr)}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      id: "targets",
      header: t("plans.assignments"),
      cell: (c) =>
        c.row.original.assignments
          .map((a) =>
            a.asset_id
              ? t("plans.oneAsset")
              : (assetTypes.data?.find((x) => x.id === a.asset_type_id)?.name ?? "?"),
          )
          .join(", "),
    },
    {
      id: "priority",
      header: t("app.priority"),
      cell: (c) => <StatusBadge value={c.row.original.priority} namespace="workOrders.priority" />,
    },
    {
      id: "schedules",
      header: t("maintenance.schedules"),
      cell: (c) => c.row.original.schedule_count ?? 0,
    },
    ...(manage
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <Button size="sm" variant="ghost" onClick={() => setDialog({ plan: c.row.original })}>
                {t("app.edit")}
              </Button>
            ),
          } satisfies ColumnDef<Plan>,
        ]
      : []),
  ];
  return (
    <div>
      <PageHeader
        title={t("plans.title")}
        subtitle={t("plans.subtitle")}
        actions={manage ? <Button onClick={() => setDialog({})}>{t("plans.new")}</Button> : null}
      />
      {dialog ? <PlanDialog plan={dialog.plan} onClose={() => setDialog(null)} /> : null}
      {plans.isPending ? (
        <LoadingState />
      ) : plans.isError ? (
        <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />
      ) : (
        <CompleteDataTable
          columns={columns}
          data={plans.data}
          getSearchText={(plan) => `${plan.code} ${plan.name}`}
          searchDescription={t("tablePilot.searchFields", {
            fields: `${t("app.code")}, ${t("app.name")}`,
          })}
          sortFields={[
            { value: "code", label: t("app.code"), valueOf: (plan) => plan.code },
            { value: "name", label: t("app.name"), valueOf: (plan) => plan.name },
          ]}
          minWidth={1000}
          getRowId={(p) => p.id}
          total={plans.data.length}
          emptyMessage={t("plans.empty")}
        />
      )}
    </div>
  );
}
